import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    // Initial check — might already be scrolled on page load
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // White text on hero (not scrolled, on home), dark text otherwise
  const useLightText = isHome && !scrolled;
  const linkClass = useLightText
    ? "text-white/90 hover:text-white hover:bg-white/15"
    : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50";
  const logoClass = useLightText
    ? "text-white"
    : "text-primary-600";
  const logoAccent = useLightText
    ? "text-accent-300"
    : "text-accent-500";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className={`font-heading text-xl md:text-2xl font-bold transition-colors duration-300 ${logoClass}`}>
              itaobuy<span className={logoAccent}>.team</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap ${linkClass}`}
            >
              Home
            </Link>

            <Link
              to="/itaobuy-sheet-guide-2026"
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap ${linkClass}`}
            >
              Shopping Guide
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer ${linkClass}`}
              >
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-background-50 border border-background-200/80 rounded-xl py-2 shadow-lg max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
              useLightText ? "text-white" : "text-foreground-700"
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-3 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-700 hover:text-primary-600"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-home-line text-sm"></i></span>
            Home
          </Link>
          <Link
            to="/itaobuy-sheet-guide-2026"
            className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-700 hover:text-primary-600"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-book-open-line text-sm"></i></span>
            Shopping Guide
          </Link>

          {/* Mobile accordion categories */}
          <div className="border-t border-background-200/70 mt-1 pt-2">
            <button
              className="flex items-center justify-between w-full py-3 text-sm font-semibold text-foreground-700 cursor-pointer"
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
            >
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-grid-line text-sm"></i></span>
                Categories
              </span>
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </span>
            </button>
            {mobileCatOpen && (
              <div className="pl-7 pb-2 space-y-1">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block py-2 text-sm text-foreground-600 hover:text-primary-600"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block mt-4 text-center px-5 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors"
          >
            Buy It Now
          </a>
        </div>
      )}
    </nav>
  );
}